// Catppuccin Palettes
// https://catppuccin.com/palette

// Catppuccin Mocha (Darkest - used for syntax token colors)
export const catppuccin = {
  rosewater: '#f5e0dc',
  flamingo: '#f2cdcd',
  pink: '#f5c2e7',
  mauve: '#cba6f7',
  red: '#f38ba8',
  maroon: '#eba0ac',
  peach: '#fab387',
  yellow: '#f9e2af',
  green: '#a6e3a1',
  teal: '#94e2d5',
  sky: '#89dceb',
  sapphire: '#74c7ec',
  blue: '#89b4fa',
  lavender: '#b4befe',
  text: '#cdd6f4',
  subtext1: '#bac2de',
  subtext0: '#a6adc8',
  overlay2: '#9399b2',
  overlay1: '#7f849c',
  overlay0: '#6c7086',
  surface2: '#585b70',
  surface1: '#45475a',
  surface0: '#313244',
  base: '#1e1e2e',
  mantle: '#181825',
  crust: '#11111b',
};

// Catppuccin Latte (Light Mode)
export const catppuccinLatte = {
  rosewater: '#dc8a78',
  flamingo: '#dd7878',
  pink: '#ea76cb',
  mauve: '#8839ef',
  red: '#d20f39',
  maroon: '#e64553',
  peach: '#fe640b',
  yellow: '#df8e1d',
  green: '#40a02b',
  teal: '#179299',
  sky: '#04a5e5',
  sapphire: '#209fb5',
  blue: '#1e66f5',
  lavender: '#7287fd',
  text: '#4c4f69',
  subtext1: '#5c5f77',
  subtext0: '#6c6f85',
  overlay2: '#7c7f93',
  overlay1: '#8c8fa1',
  overlay0: '#9ca0b0',
  surface2: '#acb0be',
  surface1: '#bcc0cc',
  surface0: '#ccd0da',
  base: '#eff1f5', // Code block background (matches mermaidTheme light)
  mantle: '#e6e9ef',
  crust: '#dce0e8',
};

export const syntaxHighlighting = {
  // Dark mode: Catppuccin Mocha-based syntax highlighting
  dark: {
    codeBlock: catppuccin.base,
    codeText: catppuccin.text,
    selection: catppuccin.surface2,
    selectionText: catppuccin.text,
    selectionAccent: catppuccin.mauve,
    highlightLineBg: catppuccin.surface0,
    highlightLineBorder: catppuccin.mauve,
    inlineCodeBg: catppuccin.surface0,
    tokenAttrName: catppuccin.yellow,
    tokenComment: catppuccin.overlay0,
    tokenString: catppuccin.green,
    tokenUrl: catppuccin.teal,
    tokenVariable: catppuccin.text,
    tokenNumber: catppuccin.peach,
    tokenBuiltin: catppuccin.blue,
    tokenChar: catppuccin.blue,
    tokenConstant: catppuccin.blue,
    tokenFunction: catppuccin.blue,
    tokenPunctuation: catppuccin.overlay2,
    tokenSelector: catppuccin.mauve,
    tokenDoctype: catppuccin.mauve,
    tokenClassName: catppuccin.yellow,
    tokenTag: catppuccin.mauve,
    tokenOperator: catppuccin.sky,
    tokenKeyword: catppuccin.mauve,
    tokenBoolean: catppuccin.peach,
    tokenProperty: catppuccin.lavender,
    tokenNamespace: catppuccin.text,
  },
  // Light mode: Catppuccin Latte-based syntax highlighting
  light: {
    codeBlock: catppuccinLatte.base, // #eff1f5 - matches mermaid diagrams
    codeText: catppuccinLatte.text,
    selection: catppuccinLatte.surface2,
    selectionText: catppuccinLatte.text,
    selectionAccent: catppuccinLatte.mauve,
    highlightLineBg: catppuccinLatte.surface0,
    highlightLineBorder: catppuccinLatte.mauve,
    inlineCodeBg: catppuccinLatte.surface0,
    tokenAttrName: catppuccinLatte.yellow,
    tokenComment: catppuccinLatte.overlay0,
    tokenString: catppuccinLatte.green,
    tokenUrl: catppuccinLatte.teal,
    tokenVariable: catppuccinLatte.text,
    tokenNumber: catppuccinLatte.peach,
    tokenBuiltin: catppuccinLatte.blue,
    tokenChar: catppuccinLatte.blue,
    tokenConstant: catppuccinLatte.blue,
    tokenFunction: catppuccinLatte.blue,
    tokenPunctuation: catppuccinLatte.overlay2,
    tokenSelector: catppuccinLatte.mauve,
    tokenDoctype: catppuccinLatte.mauve,
    tokenClassName: catppuccinLatte.yellow,
    tokenTag: catppuccinLatte.mauve,
    tokenOperator: catppuccinLatte.sky,
    tokenKeyword: catppuccinLatte.mauve,
    tokenBoolean: catppuccinLatte.peach,
    tokenProperty: catppuccinLatte.lavender,
    tokenNamespace: catppuccinLatte.text,
  },
};

// Fixed colors used by existing components.
export const colors = {
  darkBlue: '#05204A',
  blue: '#3eb0ef',
  green: '#a4d037',
  purple: '#ad26b4',
  yellow: '#fecd35',
  red: '#f05230',
  logoBg: '#091528',
  darkgrey: '#15171A',
  midgrey: '#738a94',
  lightgrey: '#c5d2d9',
  whitegrey: '#e5eff5',
  pink: '#fa3a57',
  brown: '#a3821a',
  darkmode: '#191b1f',
  darkmodeBorder: '#272a30',
  headerBg: '#090a0b',
  white: '#ffffff',
  black: '#000000',
};

// Palette values shared by the light and dark themes.
const palette = {
  neutral: {
    100: '#ffffff', // white
    150: '#fbfbf9', // offWhite (Light Bg)
    200: '#e5e5e5', // greyE5 (Dark Text)
    300: '#d4d4d4', // greyD4 (Dark Border)
    400: '#aaaaaa', // greyAAA (Dark Tertiary)
    500: '#999999', // grey999 (Dark Hover)
    600: '#888888', // grey888 (Dark Secondary)
    700: '#666666', // grey666 (Light Secondary)
    800: '#444444', // grey444 (Light Tertiary)
    900: '#111111', // charcoal (Light Text)
    950: '#1a1a1a', // darkmodeSecondary (Dark Card Bg)
    1000: '#191b1f', // darkmode (Dark Bg)
    1100: '#000000', // black
  },
  blue: {
    base: '#3eb0ef',
    selection: 'rgba(62, 176, 239, 0.45)',
  },
  ui: {
    hoverLight: 'rgba(0, 0, 0, 0.03)',
    hoverDark: 'rgba(255, 255, 255, 0.05)',
    scrollThumb: 'rgba(255, 255, 255, 0.2)',
    scrollTrack: 'rgba(0, 0, 0, 0.2)',
  },
};

// Theme-specific values for GlobalStyles and components.
export const minimalTheme = {
  light: {
    text: palette.neutral[900],
    textSecondary: palette.neutral[700],
    textTertiary: palette.neutral[800],
    heading: palette.neutral[900],
    background: palette.neutral[150],
    backgroundSecondary: palette.neutral[100],
    border: palette.neutral[900],
    accent: palette.neutral[900],
    accentHover: palette.neutral[700],
    heroAccent: palette.neutral[900],
    heroInk: catppuccinLatte.lavender,
    heroShade: catppuccinLatte.surface2,
    heroKey: '#c2410c', // darker than Latte peach: 5:1 on the page background
    codeBackground: catppuccinLatte.base, // Matches mermaid diagrams
    codeText: catppuccinLatte.text,
    cardBackground: palette.neutral[100],
    cardBorder: palette.neutral[1100],
    cardShadow: palette.neutral[1100],
    invertedBackground: palette.neutral[100],
    hoverBackground: palette.ui.hoverLight,
  },
  dark: {
    text: palette.neutral[200],
    textSecondary: palette.neutral[600],
    textTertiary: palette.neutral[400],
    heading: palette.neutral[200],
    background: palette.neutral[1000],
    backgroundSecondary: palette.neutral[950],
    border: palette.neutral[200],
    accent: palette.neutral[200],
    accentHover: palette.neutral[500],
    heroAccent: catppuccin.peach,
    heroInk: catppuccin.lavender,
    heroShade: catppuccin.surface2,
    heroKey: catppuccin.peach,
    codeBackground: catppuccin.base,
    codeText: catppuccin.text,
    cardBackground: palette.neutral[950],
    cardBorder: palette.neutral[300],
    cardShadow: palette.neutral[300],
    invertedBackground: palette.neutral[900], // charcoal for dark text on light bg
    hoverBackground: palette.ui.hoverDark,
  },
  common: {
    link: 'inherit',
    linkHover: 'inherit',
    selection: palette.blue.selection,
    scrollbarThumb: palette.ui.scrollThumb,
    scrollbarTrack: palette.ui.scrollTrack,
  },
};

// CSS variables defined by GlobalStyles.tsx for system and manual themes.
export const theme = {
  // Text colors
  text: 'var(--color-text)',
  textSecondary: 'var(--color-text-secondary)',
  textTertiary: 'var(--color-text-tertiary)',

  // Heading colors
  heading: 'var(--color-heading)',

  // Background colors
  background: 'var(--color-background)',
  backgroundSecondary: 'var(--color-background-secondary)',

  // Border colors
  border: 'var(--color-border)',

  // Accent/interactive colors
  accent: 'var(--color-accent)',
  accentHover: 'var(--color-accent-hover)',
  heroAccent: 'var(--color-hero-accent)',
  heroInk: 'var(--color-hero-ink)',
  heroShade: 'var(--color-hero-shade)',
  heroKey: 'var(--color-hero-key)',

  // Link colors
  link: 'var(--color-link)',
  linkHover: 'var(--color-link-hover)',

  // Code block colors
  codeBackground: 'var(--color-code-background)',
  codeText: 'var(--color-code-text)',

  // Card colors (for featured cards, etc.)
  cardBackground: 'var(--color-card-background)',
  cardBorder: 'var(--color-card-border)',
  cardShadow: 'var(--color-card-shadow)',

  // Inverted background (for hover states with accent background)
  invertedBackground: 'var(--color-inverted-background)',

  // Hover background (for subtle hover effects)
  hoverBackground: 'var(--color-hover-background)',
};

// Syntax highlighting colors
// These are used for code blocks and syntax highlighting

// Shadow and overlay utilities
// These use rgba() with base colors for shadows, borders, and overlays
export const shadows = {
  // Light theme shadows (black with opacity)
  light: {
    subtle: 'rgba(0, 0, 0, 0.08)', // Very light shadow
    default: 'rgba(0, 0, 0, 0.12)', // Standard shadow
    medium: 'rgba(0, 0, 0, 0.2)', // Medium shadow
    strong: 'rgba(0, 0, 0, 0.3)', // Strong shadow
    // Border/overlay
    border: 'rgba(0, 0, 0, 0.1)', // Border color
    overlay: 'rgba(0, 0, 0, 0)', // Transparent (for gradients)
  },
  // Dark theme shadows (black with opacity - stronger)
  dark: {
    subtle: 'rgba(0, 0, 0, 0.2)', // Light shadow in dark mode
    default: 'rgba(0, 0, 0, 0.3)', // Standard shadow in dark mode
    medium: 'rgba(0, 0, 0, 0.4)', // Medium shadow in dark mode
    strong: 'rgba(0, 0, 0, 0.5)', // Strong shadow in dark mode
    // Border/overlay
    border: 'rgba(0, 0, 0, 0.2)', // Border color in dark mode
    overlay: 'rgba(0, 0, 0, 0)', // Transparent (for gradients)
  },
  // White overlays for dark backgrounds
  white: {
    subtle: 'rgba(255, 255, 255, 0.05)', // Very subtle white overlay
    default: 'rgba(255, 255, 255, 0.1)', // Standard white overlay
    medium: 'rgba(255, 255, 255, 0.15)', // Medium white overlay
  },
};

// Blue accent color utilities (using colors.blue with opacity)
export const blueAccent = {
  light: {
    subtle: 'rgba(62, 176, 239, 0.08)', // Very light blue background
    default: 'rgba(62, 176, 239, 0.12)', // Light blue background
    border: 'rgba(62, 176, 239, 0.15)', // Light blue border
    medium: 'rgba(62, 176, 239, 0.2)', // Medium blue border
    borderHover: 'rgba(62, 176, 239, 0.3)', // Light blue border on hover
    text: 'rgba(62, 176, 239, 0.9)', // Blue text with slight transparency
    solid: 'rgba(62, 176, 239, 1)', // Solid blue
  },
  dark: {
    subtle: 'rgba(62, 176, 239, 0.12)', // Dark mode light blue background
    default: 'rgba(62, 176, 239, 0.18)', // Dark mode medium blue background
    border: 'rgba(62, 176, 239, 0.2)', // Dark mode blue border
    medium: 'rgba(62, 176, 239, 0.25)', // Dark mode medium blue border
    strong: 'rgba(62, 176, 239, 0.3)', // Dark mode strong blue border
    borderHover: 'rgba(62, 176, 239, 0.5)', // Dark mode blue border on hover
    text: 'rgba(62, 176, 239, 0.9)', // Blue text with slight transparency
    solid: 'rgba(62, 176, 239, 1)', // Solid blue
  },
};

// Badge colors (for "New" badges, etc.)
export const badge = {
  yellow: {
    background: '#fbd45b', // Bright yellow background for "New" badge
    text: '#111', // Dark text color for yellow badge
    gradientOverlay: 'rgba(255, 255, 255, 0.3)', // White overlay for gradient effect
  },
};
